import React, { createContext, useContext, useState, useMemo } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, ListChecks, BarChart3, Users, Settings, LifeBuoy, LogOut, X, ShieldCheck, Building2, LayoutGrid, Wallet, CreditCard, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo, { PulseMark } from "@/components/brand/Logo";
import MobileBottomNav from "@/pages/dashboard/MobileBottomNav";

const DashCtx = createContext(null);
export const useDash = () => useContext(DashCtx);

const WHATSAPP = "918839790836";

export default function DashboardLayout() {
  const { user, business, isAdmin, logout, impersonating, effectiveBusinessId } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedVenue, setSelectedVenue] = useState(null);

  const isEmployee = user?.role === "employee";
  const businessId = effectiveBusinessId;

  const { data: venueData, isLoading: venuesLoading, refetch: refetchVenues } = useQuery({
    queryKey: ["venues", businessId],
    queryFn: async () => (await api.get("/venues", { params: businessId ? { business_id: businessId } : {} })).data,
    enabled: !!businessId,
  });

  const venues = venueData?.venues || [];
  const businessName = venueData?.business?.name || business?.name || (impersonating?.name);
  const employeeExpenseAccess = !!venueData?.business?.employee_expense_access;
  const perms = user?.permissions;
  const can = {
    revenue: !isEmployee || !!perms?.view_revenue,
    payments: !isEmployee || !!perms?.view_payments,
    pnl: !isEmployee ? true : (perms ? !!perms.manage_pnl : employeeExpenseAccess),
    delete: !isEmployee || !!perms?.delete_bookings,
  };
  const pnlAccess = can.pnl && !!businessId;

  const nav = useMemo(() => {
    const items = [
      { to: "/dashboard/calendar", label: "Calendar", icon: CalendarDays },
      { to: "/dashboard/bookings", label: "Bookings", icon: ListChecks },
    ];
    if (!isEmployee) {
      items.unshift({ to: "/dashboard", label: "Home", icon: LayoutGrid, end: true });
    }
    if (businessId && can.revenue) {
      items.push({ to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 });
    }
    if (!isEmployee && businessId) {
      items.push({ to: "/dashboard/employees", label: "Employees", icon: Users });
    }
    if (businessId && can.pnl) {
      items.push({ to: "/dashboard/profit-loss", label: "Profit & Loss", icon: Wallet });
    }
    if (businessId && can.payments) {
      items.push({ to: "/dashboard/payments", label: "Payment Methods", icon: CreditCard });
      items.push({ to: "/dashboard/pending", label: "Pending Payments", icon: Clock });
    }
    items.push({ to: "/dashboard/settings", label: "Settings", icon: Settings });
    return items;
  }, [isEmployee, businessId, can.revenue, can.pnl, can.payments]);

  // Admin without an impersonated business
  if (isAdmin && !businessId) {
    return (
      <div className="grid min-h-screen place-items-center bg-cloud p-6">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-10 text-center shadow-pulse-md">
          <div className="mx-auto grid w-fit place-items-center rounded-2xl bg-pulse-navy p-4"><ShieldCheck className="text-pulse-green" size={28} /></div>
          <h2 className="mt-5 text-2xl font-bold text-pulse-navy">Admin mode</h2>
          <p className="mt-2 text-sm text-slate">Pick a business to view its calendar, or open the admin panel to manage clients.</p>
          <button onClick={() => navigate("/admin")} data-testid="go-admin-panel-btn"
            className="mt-6 w-full rounded-md bg-pulse-navy px-5 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98]">
            Open Admin Panel
          </button>
        </div>
      </div>
    );
  }

  // Owner/employee not yet provisioned → still show the hub so they can start a trial

  const SidebarInner = (
    <div className="flex h-full flex-col bg-pulse-navy text-white grain-overlay">
      <div className="relative z-10 flex items-center justify-between px-5 py-5">
        <button onClick={() => navigate("/")} className="transition-opacity hover:opacity-80"><Logo variant="light" size={24} /></button>
        <button className="lg:hidden text-white/70" onClick={() => setSidebarOpen(false)}><X size={20} /></button>
      </div>

      <nav className="relative z-10 flex-1 space-y-1 px-3 py-2">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setSidebarOpen(false)}
            data-testid={`sidebar-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3.5 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
              }`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>

      <div className="relative z-10 space-y-1 border-t border-white/10 px-3 py-4">
        <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noopener noreferrer"
          data-testid="sidebar-support"
          className="flex items-center gap-3 rounded-md px-3.5 py-2.5 text-sm font-medium text-white/60 transition-colors hover:bg-white/5 hover:text-white">
          <LifeBuoy size={18} /> Support
        </a>
        <button onClick={logout} data-testid="sidebar-logout"
          className="flex w-full items-center gap-3 rounded-md px-3.5 py-2.5 text-sm font-medium text-white/60 transition-colors hover:bg-white/5 hover:text-white">
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </div>
  );

  const ctx = { businessId, venues, venuesLoading, refetchVenues, selectedVenue, setSelectedVenue,
                monthDate, setMonthDate, isEmployee, user, businessName, isAdmin, impersonating,
                can, businessHasLogo: venueData?.business?.has_logo };

  return (
    <DashCtx.Provider value={ctx}>
      <div className="flex min-h-screen bg-cloud">
        {/* Desktop sidebar */}
        <aside className="hidden w-64 shrink-0 lg:block"><div className="fixed h-screen w-64">{SidebarInner}</div></aside>

        {/* Mobile sidebar */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-64">{SidebarInner}</div>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Top bar */}
          <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/85 px-4 backdrop-blur-xl sm:px-6">
            <div className="flex items-center gap-3">
              <div>
                <div className="text-sm font-semibold text-pulse-navy" data-testid="dash-business-name">{businessName}</div>
                <div className="text-xs text-slate capitalize">{isAdmin && impersonating ? "Admin view" : user?.role}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isAdmin && impersonating && (
                <button onClick={() => navigate("/admin")} data-testid="exit-impersonation-btn"
                  className="hidden rounded-md bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800 sm:block">
                  Viewing as owner · Exit
                </button>
              )}
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full border border-slate-200 object-cover" />
              ) : (
                <div className="grid h-9 w-9 place-items-center rounded-full bg-pulse-navy"><PulseMark size={18} color="#FFFFFF" /></div>
              )}
            </div>
          </header>

          {isAdmin && impersonating && (
            <div className="bg-amber-50 px-4 py-2 text-center text-xs font-medium text-amber-800 sm:px-6">
              You are impersonating <b>{businessName}</b>. All actions are logged.
            </div>
          )}

          <main className="flex-1 p-4 pb-24 sm:p-6 sm:pb-24 lg:p-8 lg:pb-8"><Outlet /></main>
          <MobileBottomNav isEmployee={isEmployee} showRevenue={can.revenue} showPnl={can.pnl} showPayments={can.payments} onMore={() => setSidebarOpen(true)} />
        </div>
      </div>
    </DashCtx.Provider>
  );
}
